import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Table } from "antd";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import { useLazyGetMyPayslipsQuery } from "../../../../services/payroll.service";
import { formatNumber } from "../../../../utils";
import dayjs from "dayjs";

const MyPayslips = () => {
  const [results, setResult] = useState([]);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    year: null,
    month: null,
  });
  const navigate = useNavigate();

  const [getMyPayslips, { data, isFetching }] = useLazyGetMyPayslipsQuery();

  useEffect(() => {
    // Load payslips on mount
    getMyPayslips({ page: 1, limit: 10 });
  }, []);

  useEffect(() => {
    if (data?.success) {
      const payslips = Array.isArray(data?.data?.result) ? data.data.result : [];
      setResult(payslips);
      setPagination(data?.data?.meta || {});
    }
  }, [data]);

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    getMyPayslips({ ...newFilters, page: 1, limit: 10 });
  };

  const handleViewPayslip = (payslipId) => {
    // Navigate to payslip detail view
    navigate(`/payslip/${payslipId}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Draft":
        return "secondary";
      case "Pending":
        return "info";
      case "Approved":
        return "success";
      case "Paid":
        return "primary";
      case "Rejected":
        return "danger";
      default:
        return "secondary";
    }
  };

  const getMonthName = (month) => {
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    return months[month - 1] || "";
  };

  // Calculate statistics from payslips
  const stats = {
    totalPayslips: results.length,
    totalGrossPay: results
      .filter((p) => p.status === "Paid")
      .reduce((sum, payslip) => sum + parseFloat(payslip.gross_pay || 0), 0),
    totalNetPay: results
      .filter((p) => p.status === "Paid")
      .reduce((sum, payslip) => sum + parseFloat(payslip.net_pay || 0), 0),
    totalDeductions: results
      .filter((p) => p.status === "Paid")
      .reduce((sum, payslip) => sum + parseFloat(payslip.total_deductions || 0), 0),
  };

  const statsData = [
    {
      title: "Total Payslips",
      value: stats.totalPayslips,
      icon: "la la-file-text",
      color: "primary",
    },
    {
      title: "Total Gross Pay",
      value: `₦${formatNumber(stats.totalGrossPay)}`,
      icon: "la la-money",
      color: "success",
    },
    {
      title: "Total Net Pay",
      value: `₦${formatNumber(stats.totalNetPay)}`,
      icon: "la la-credit-card",
      color: "info",
    },
    {
      title: "Total Deductions",
      value: `₦${formatNumber(stats.totalDeductions)}`,
      icon: "la la-minus-circle",
      color: "danger",
    },
  ];

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      render: (t, r, i) => i + 1,
      width: 50,
    },
    {
      title: "Period",
      dataIndex: "month",
      render: (text, record) => (
        <span className="fw-bold">
          {getMonthName(record.month)} {record.year}
        </span>
      ),
      sorter: (a, b) => {
        const dateA = new Date(a.year, a.month - 1);
        const dateB = new Date(b.year, b.month - 1);
        return dateA - dateB;
      },
    },
    {
      title: "Gross Pay",
      dataIndex: "gross_pay",
      render: (text) => <span>₦{formatNumber(text)}</span>,
      sorter: (a, b) => parseFloat(a.gross_pay) - parseFloat(b.gross_pay),
    },
    {
      title: "Deductions",
      dataIndex: "total_deductions",
      render: (text) => <span className="text-danger">₦{formatNumber(text)}</span>,
      sorter: (a, b) => parseFloat(a.total_deductions) - parseFloat(b.total_deductions),
    },
    {
      title: "Net Pay",
      dataIndex: "net_pay",
      render: (text) => <span className="fw-bold text-success">₦{formatNumber(text)}</span>,
      sorter: (a, b) => parseFloat(a.net_pay) - parseFloat(b.net_pay),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text) => (
        <span className={`badge bg-inverse-${getStatusColor(text)}`}>
          {text}
        </span>
      ),
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
    {
      title: "Generated On",
      dataIndex: "created_at",
      render: (text) => <span>{dayjs(text).format("DD MMM YYYY")}</span>,
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: "Action",
      dataIndex: "id",
      render: (text, record) => (
        <div className="dropdown dropdown-action text-center">
          <Link
            to="#"
            className="action-icon dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="material-icons">more_vert</i>
          </Link>
          <div className="dropdown-menu dropdown-menu-right">
            <Link
              className="dropdown-item"
              to="#"
              onClick={() => handleViewPayslip(record.id)}
            >
              <i className="fa fa-eye m-r-5" /> View Payslip
            </Link>
            <Link
              className="dropdown-item"
              to="#"
              onClick={() => window.open(`/api/v2/payroll/${record.id}/download`, '_blank')}
            >
              <i className="fa fa-download m-r-5" /> Download PDF
            </Link>
          </div>
        </div>
      ),
    },
  ];

  // Generate year options (current year and 3 years back)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 4 }, (_, i) => currentYear - i);

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
          <Breadcrumbs
            maintitle="My Payslips"
            title="Dashboard"
            subtitle="My Payslips"
            showButton={false}
          />

          {/* Statistics */}
          <div className="row">
            {statsData.map((stat, index) => (
              <div className="col-md-6 col-sm-6 col-lg-6 col-xl-3" key={index}>
                <div className="card dash-widget">
                  <div className="card-body">
                    <span className="dash-widget-icon">
                      <i className={stat.icon} />
                    </span>
                    <div className="dash-widget-info">
                      <h3>{stat.value}</h3>
                      <span>{stat.title}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Filter Section */}
          <div className="row filter-row mb-3">
            <div className="col-sm-6 col-md-3">
              <div className="input-block mb-3 form-focus select-focus">
                <select
                  className="form-select floating"
                  value={filters.year || ""}
                  onChange={(e) => handleFilterChange("year", e.target.value || null)}
                >
                  <option value="">All Years</option>
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <label className="focus-label">Year</label>
              </div>
            </div>
            <div className="col-sm-6 col-md-3">
              <div className="input-block mb-3 form-focus select-focus">
                <select
                  className="form-select floating"
                  value={filters.month || ""}
                  onChange={(e) => handleFilterChange("month", e.target.value || null)}
                >
                  <option value="">All Months</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                    <option key={month} value={month}>
                      {getMonthName(month)}
                    </option>
                  ))}
                </select>
                <label className="focus-label">Month</label>
              </div>
            </div>
          </div>

          {/* Payslips Table */}
          <div className="row">
            <div className="col-md-12">
              <div className="table-responsive">
                <Table
                  className="table-striped"
                  columns={columns}
                  loading={isFetching}
                  dataSource={results}
                  rowKey={(record) => record.id}
                  pagination={{
                    defaultPageSize: 10,
                    total: pagination?.total || results.length,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`,
                    onChange: (page, pageSize) => {
                      getMyPayslips({ ...filters, page, limit: pageSize });
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MyPayslips;
